import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Trash2, Shield, Mail, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user: ${userEmail}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      alert(`User ${userEmail} deleted successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: 'bg-red-100 text-red-800 border-red-300',
      admin: 'bg-purple-100 text-purple-800 border-purple-300',
      user: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    const labels = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      user: 'User'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${badges[role] || badges.user}`}>
        {labels[role] || role}
      </span>
    );
  };

  // Only super admin can access this page
  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-900 mb-2">Access Denied</h2>
            <p className="text-red-700">Only Super Administrators can access user management.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
            User Management
          </h2>
          <p className="text-gray-600 mt-2">Manage all registered users (Super Admin Only)</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Total Users</p>
            <div className="text-3xl font-black">{users.length}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Super Admins</p>
            <div className="text-3xl font-black">{users.filter(u => u.role === 'super_admin').length}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Admins</p>
            <div className="text-3xl font-black">{users.filter(u => u.role === 'admin').length}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Regular Users</p>
            <div className="text-3xl font-black">{users.filter(u => u.role === 'user').length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="shadow-lg">
              <CardContent className="p-6">
                <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-6 h-6 mr-2" />
              All Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.map((userItem) => (
                <div
                  key={userItem.id}
                  className="flex items-center justify-between p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-orange-200 hover:shadow-md transition-all"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{userItem.name}</h3>
                      {getRoleBadge(userItem.role)}
                      {userItem.id === user.id && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-semibold">
                          You
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Mail className="w-4 h-4 mr-1" />
                        {userItem.email}
                      </span>
                      {userItem.created_at && (
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Joined: {new Date(userItem.created_at).toLocaleDateString()}
                        </span>
                      )}
                      {userItem.client_count !== undefined && (
                        <span className="font-semibold text-blue-600">
                          {userItem.client_count} Clients
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="ml-4">
                    {userItem.id !== user.id ? (
                      <Button
                        onClick={() => handleDeleteUser(userItem.id, userItem.email)}
                        variant="outline"
                        size="sm"
                        className="border-2 border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete User
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="opacity-50 cursor-not-allowed"
                      >
                        Cannot Delete Self
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {users.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No users found</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Warning Notice */}
      <Card className="bg-yellow-50 border-2 border-yellow-200">
        <CardContent className="p-6">
          <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes:</h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Deleting a user is permanent and cannot be undone</li>
            <li>• All user data (clients, tasks, invoices, etc.) will remain in the system</li>
            <li>• You cannot delete your own account</li>
            <li>• Only Super Administrators can access this page</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
